import React, { useEffect, useState } from 'react';
import classnames from 'classnames';

import styles from './search-bar.css';
import { SearchIcon, ClearSearchIcon } from '../icons/icons';
import { IconButton } from '../actions';
import { Icon } from '../icon';
import Colors from '../colors';

interface SearchBarProps {
  /** Disables search bar disallowing interaction */
  disabled?: boolean;
  /** Placeholder text to show inside the search bar */
  placeholder?: string;
  /** Adds additional classes to the component. */
  className?: string;
  /** Search bar value, it should be accompanied by an onChange function */
  value?: string;
  /** Name added to the input field in the HTML Markup */
  name?: string;
  /** Provides visual elements for usage on a dark environment */
  darkEnv?: boolean;
  /** Provides visual styles for a smaller input field */
  mini?: boolean;
  /** Callback when typed on */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback when focused */
  onFocus?: () => void;
  /** Callback when unfocused */
  onBlur?: () => void;
  /** Callback when the user hits enter or the input is submitted */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Defines if the text inside the search bar should be cleared after submit or not */
  clearAfterSubmit?: boolean;
  /** Callback when user hits the clear button */
  onClear?: () => void;
  /** Provides a data-testid */
  'data-testid'?: string;
}

/**
 * SearchBar Component
 *
 * @param {SearchBarProps} props
 *
 * @returns JSX.ELEMENT
 */
export const SearchBar = ({
  placeholder,
  className,
  onChange,
  onFocus,
  onBlur,
  onSubmit,
  onClear,
  value: searchValue,
  'data-testid': dataTestid,
  disabled = false,
  darkEnv = false,
  mini = false,
  name = 'search',
  clearAfterSubmit = true
}: SearchBarProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState(searchValue || '');

  useEffect(() => {
    setValue(searchValue || '');
  }, [searchValue]);

  const searchClasses = classnames(styles.search, className, {
    [styles.dark]: darkEnv,
    [styles.disabled]: disabled,
    [styles.focus]: isFocused,
    [styles.mini]: mini
  });
  const clearSearchClasses = classnames(styles.clearSearch, {
    [styles.clearSearchHidden]: value === ''
  });
  const iconSize = '16px';
  const miniIconSize = '12px';
  const miniClearSearchSize = '12px';

  const clearSearch = () => {
    if (onClear) {
      onClearCallback(onClear);
    }
    setValue('');
  };

  const handleBlur = () => {
    onBlur?.();
    setIsFocused(false);
  };

  const handleFocus = () => {
    onFocus?.();
    setIsFocused(true);
  };

  const getIconColor = () => {
    return disabled
      ? darkEnv
        ? Colors.Emperor
        : Colors.SilverAlto
      : isFocused
      ? Colors.UnbounceBlue
      : darkEnv
      ? Colors.Gallery
      : Colors.Scorpion;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.value);

    if (onChange) {
      onChange(e);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (onSubmit && !disabled) {
      onSubmit(e);
    }

    if (clearAfterSubmit) {
      clearSearch();
    }
  };

  const onClearCallback = (e: () => void) => e();

  return (
    <form
      className={searchClasses}
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => handleSubmit(e)}
      data-testid={dataTestid}
    >
      <Icon className={styles.icon} color={getIconColor()} size={mini ? miniIconSize : iconSize}>
        <SearchIcon />
      </Icon>
      <input
        className={styles.input}
        type="text"
        disabled={disabled}
        {...(placeholder && { placeholder })}
        name={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={value}
      />
      <IconButton
        className={clearSearchClasses}
        color={Colors.GullGray_DEPRECATED}
        size={mini ? miniClearSearchSize : iconSize}
        onClick={clearSearch}
      >
        <ClearSearchIcon />
      </IconButton>
    </form>
  );
};

export default SearchBar;
